import { getTextWidth } from 'components/common/size_helpers';
import { debounce } from 'lodash';
import { mapColorFromColorPickerToDarkMode } from 'javascripts/theme';
import tinycolor from 'tinycolor2';
import { formatDisplayValue } from './util';
import AhaDrawer from 'javascripts/drawer';

const FILL_AND_LABEL_COLORS = new Map([
  ['var(--aha-blue-500)', 'white'],
  ['var(--aha-red-600)', 'var(--aha-gray-900)'],
  ['var(--aha-green-600)', 'var(--aha-gray-900)'],
  ['var(--aha-purple-300)', 'white'],
  ['var(--aha-orange-600)', 'var(--aha-gray-900)'],
  ['var(--aha-pink-600)', 'white'],
  ['var(--aha-yellow-600)', 'var(--aha-gray-900)'],
  ['var(--aha-teal-600)', 'white'],
  ['var(--aha-orange-500)', 'var(--aha-gray-900)'],
  ['var(--aha-blue-400)', 'var(--aha-gray-900)'],
  ['var(--aha-pink-700)', 'white'],
  ['var(--aha-teal-400)', 'var(--aha-gray-900)'],
]);

export const FILL_COLORS = Array.from(FILL_AND_LABEL_COLORS.keys());

export const BLANK_FILL = 'var(--aha-gray-400)';

export function formatAxis({ configuration, axis }) {
  const { orientation, scale, chartType } = configuration;

  const horizontalBarChart =
    chartType === 'BarChart' && orientation === 'horizontal';

  const yAxisInVerticalChart = axis === 'y' && !horizontalBarChart;
  const xAxisInHorizontalChart = axis === 'x' && horizontalBarChart;

  const isPrimaryAxis = yAxisInVerticalChart || xAxisInHorizontalChart;

  const prefixKey = isPrimaryAxis ? 'axisPrefix' : 'secondaryAxisPrefix';
  const suffixKey = isPrimaryAxis ? 'axisSuffix' : 'secondaryAxisSuffix';

  const axisPrefix = configuration[prefixKey];
  const axisSuffix = configuration[suffixKey];

  return value => {
    // Certain charts are not guaranteed to have a numerical value for their
    // axis tick, so we should not try to scale them.
    const scaledValue =
      typeof value === 'number' ? value / (scale || 1) : value;

    return [axisPrefix, formatDisplayValue(scaledValue), axisSuffix].join('');
  };
}

const colorMap = {};
export function contrastingTextColor(color) {
  if (colorMap[color]) return colorMap[color];

  if (color === undefined || color === null) {
    return "var('--theme-primary-text')";
  }

  const colorHex = color.replace('#', '');
  const r = parseInt(colorHex.substring(0, 2), 16);
  const g = parseInt(colorHex.substring(2, 4), 16);
  const b = parseInt(colorHex.substring(4, 6), 16);

  let a = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  colorMap[color] = a < 0.5 ? 'var(--aha-gray-900)' : 'white';
  return colorMap[color];
}

export function generateDefaultConfiguration({ configuration, meta, width }) {
  const approximateLegendWidth = calculateLegendWidth({ width, meta });

  const { chartType, groupMode, orientation, backgroundColor, gridColor } =
    configuration;
  let seriesLabels = meta.seriesLabels;
  if (
    chartType === 'BarChart' &&
    groupMode === 'stacked' &&
    orientation === 'vertical'
  ) {
    seriesLabels = [...seriesLabels].reverse();
  }

  const chartConfig = {
    legends: configuration.legendAnchor
      ? [
          {
            dataFrom: 'keys',
            anchor: configuration.legendAnchor,
            direction: 'column',
            justify: false,
            translateX: approximateLegendWidth + 20,
            itemWidth: approximateLegendWidth,
            itemHeight: 20,
            symbolSize: 10,
          },
        ]
      : [],
    theme: {
      textColor: 'var(--theme-primary-text)',
      background:
        backgroundColor === 'transparent'
          ? 'var(--theme-primary-background)'
          : backgroundColor,
      tooltip: {
        container: {
          background: 'var(--theme-chart-tooltip-background)',
          color: 'var(--theme-primary-text)',
        },
      },
      fontFamily: null, // Set this to null so that we inherit font from our normal CSS.
      fontSize: 12,
      legends: {
        text: {
          fontSize: 12,
          fill: 'var(--aha-gray-800)',
        },
      },
      axis: {
        ticks: {
          line: { stroke: 'var(--theme-light-border)' },
          text: { fill: 'var(--theme-secondary-text)' },
        },
        legend: {
          text: {
            fontSize: 14,
            fontWeight: 'bold',
            fill: 'var(--aha-gray-800)',
          },
        },
      },
      grid: {
        line: {
          stroke:
            gridColor === 'transparent'
              ? 'var(--theme-light-border)'
              : gridColor,
        },
      },
      dots: { text: { color: 'var(--theme-primary-text)' } },
    },
    colors: Array.from(FILL_AND_LABEL_COLORS.keys()),
    axisLeft: {
      tickSize: 0,
      tickPadding: 15,
      legendPosition: 'middle',
      legendOffset: -80,
      format: formatAxis({ configuration, axis: 'y' }),
      legend: configuration.verticalAxis?.title,
      tickRotation: configuration.verticalAxis?.labelSlant || 0,
    },
    axisBottom: {
      tickSize: 0,
      tickPadding: 15,
      legendPosition: 'middle',
      legendOffset: 80,
      format: formatAxis({ configuration, axis: 'x' }),
      legend: configuration.horizontalAxis?.title,
      tickRotation: -configuration.horizontalAxis?.labelSlant || 0,
    },
    animate: false,
    indexBy: meta.columnKey,
    keys: seriesLabels,
    labelTextColor: ({ color }) =>
      FILL_AND_LABEL_COLORS.get(color) || contrastingTextColor(color),
    minValue: configuration.minValue || 'auto',
    maxValue: configuration.maxValue || 'auto',
    enableGridX: configuration.orientation === 'horizontal',
    enableGridY: configuration.orientation === 'vertical',
  };

  // Handle labels for each chart type, arcLabels, and arcLinkLabels
  if (configuration.showLabels === 'false') {
    chartConfig.labelFormat = () => null;
  }

  if (!configuration.horizontalAxis?.enabled) {
    chartConfig.axisBottom = null;
  }

  if (!configuration.verticalAxis?.enabled) {
    chartConfig.axisLeft = null;
  }

  return chartConfig;
}

export function getColor(configuration, meta, series) {
  const { seriesColors, seriesLabels, seriesLabelsToIds } = meta;
  const { colors, useDataColor } = configuration;
  series = trimDeduplicatedLabel(series);
  const seriesId = seriesLabelsToIds[series];

  // Use series name as color key in configuration. Fall back to seriesId to
  // support charts created before this change. See feature ADT-222.
  const customColor = colors?.[series] || colors?.[seriesId];
  let color = '';
  if (customColor) {
    color = mapColorFromColorPickerToDarkMode(customColor);
  } else if (series) {
    const seriesIndex = seriesLabels.indexOf(series);
    const dataColor = useDataColor && seriesColors[seriesId];
    color = dataColor || FILL_COLORS[seriesIndex % FILL_COLORS.length];
  }
  color ||= BLANK_FILL;

  // HACK:
  // This is a gnarly one to work around a react-spring idiosyncrasy. Even
  // though animations are disabled on all charts, Nivo still uses react-spring
  // to set colors and such. react-spring, strangely enough, still attempts to
  // validate that oldColor and newColor (as it were) can be transitioned
  // smoothly between. Since chart custom colors are hex, but built-in colors
  // are CSS var(--aha) colors, this validation fails, and react-spring throws
  // an error saying, essentially, "I can't animate that!" — even though we
  // are *not* animating it.
  //
  // So this hack figures out what the Aha! color maps to in terms of rgb(),
  // and then converts it to hex, so that all colors are in hex and react-spring
  // doesn't complain.
  if (color && color.includes('var(--')) {
    const variableName = color.match(/var\(([^)]+)\)/)[1];
    const bodyStyle = getComputedStyle(document.body);
    const rgbColor = bodyStyle.getPropertyValue(variableName);
    color = tinycolor(rgbColor).toHexString();
  }

  return color;
}

/**
 * Returns a slightly lighter or darker color from the one passed in
 * @param {string} color - tinycolor can accept many different color formats
 * @returns {string}
 */
function getGridColorFromChartBackground(color) {
  const gridColor = tinycolor(color);

  if (gridColor.isDark()) {
    // increase contrast for very dark (black) colors
    gridColor.getBrightness() < 10
      ? gridColor.lighten(15)
      : gridColor.lighten(7);
  } else {
    // increase contrast for very light colors (e.g., yellows/greens)
    gridColor.getLuminance() > 0.5 ? gridColor.darken(8) : gridColor.darken(5);
  }

  // toString ensures we get a hex format: #<hex color>
  return gridColor.toString();
}

/**
 * Set background and grid colors for chart config.
 * @param {string} color
 * @param {*=} merge - ConfiguredReport merge function
 */
export function setBackgroundAndGridColors(color, merge) {
  merge({
    backgroundColor: color,
    gridColor: getGridColorFromChartBackground(color),
  });
}

export function approximateWidth(content) {
  // The widest character in the font is W, which is ~13px wide. 7px seems like
  // a happy medium for an average-sized character in the font. This just gives
  // us a vague idea of how wide the legend text might be so that we can adjust
  // margins accordingly.
  return 8 * content;
}

export function round(number, decimalPlaces = 2) {
  const powerOf10 = 10 ** decimalPlaces;

  return Math.round((number + Number.EPSILON) * powerOf10) / powerOf10;
}

const MIN_LEGEND_WIDTH = 40;
const LEGEND_PADDING = 20;
const legendWidths = {};

export function calculateLegendWidth({ meta, width = null }) {
  meta.seriesLabels.forEach(label => {
    if (legendWidths[label]) return;
    legendWidths[label] = getTextWidth(
      label,
      null,
      null,
      'custom-pivot-chart__legend-item'
    );
  });

  let approximateLegendWidth = Math.max(
    ...Object.values(legendWidths).map(dims => dims.width + LEGEND_PADDING),
    MIN_LEGEND_WIDTH
  );

  if (width) {
    approximateLegendWidth = Math.min(approximateLegendWidth, width / 4);
  }

  return approximateLegendWidth;
}

export const saveConfiguration = debounce(({ configuration, bookmarkId }) => {
  saveConfigurationImmediately({ bookmarkId, configuration });
}, 500);

export function saveConfigurationImmediately({ configuration, bookmarkId }) {
  return fetch(`/bookmarks/custom_pivots/${bookmarkId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document
        .querySelector('meta[name=csrf-token]')
        .getAttribute('content'),
    },
    body: JSON.stringify({
      bookmarks_custom_pivot: configuration,
      reactive_client_id: window.reactiveClientId(),
    }),
  });
}

export function seriesIsEmpty({ meta }) {
  return !meta.reportColumnNames.series;
}

/**
 * @param {*} meta - meta data for chart
 * @param {*} series - series for data point (slice for pie chart, x-axis field for line chart)
 * @returns {string} - series label display value, which for numbers will be comma delimited string, everything else will be the same value as series param
 */
export function getDisplayValuesForSeries(meta, series) {
  series = trimDeduplicatedLabel(series);
  const seriesDisplayLabel = meta.seriesLabelsToDisplayLabels[series] || '';

  return seriesDisplayLabel;
}

/**
 * Provide display value for datum by checking for series display value if
 * available, then display value, then finally the plain value of the datum
 * @param {import('@nivo/pie').ComputedDatum | import('@nivo/bar').BarDatum } datum
 * @returns {string} display_value
 */
export function getDisplayValueForDatum(datum) {
  const { id, data } = datum;
  return data[`${id}_display_value`] || data['display_value'] || datum.value;
}

const filterSeriesKeys = (column, metaColumnKey) => {
  return Object.keys(column).filter(
    key =>
      key !== metaColumnKey &&
      key !== 'row_label' &&
      !key.includes('display_value') &&
      key !== 'column_index'
  );
};

export function formatBarChartData({ data, meta, percentStacking, reversed }) {
  let result = [...data];
  if (percentStacking) {
    result = data.map(column => {
      const newColumn = { [meta.columnKey]: column[meta.columnKey] };
      const series = filterSeriesKeys(column, meta.columnKey);

      const columnTotal = series.reduce((sum, seriesKey) => {
        return sum + column[seriesKey];
      }, 0);

      series.forEach(seriesKey => {
        newColumn[seriesKey] = columnTotal
          ? round((column[seriesKey] / columnTotal) * 100.0)
          : 0;
      });

      // preserve labels for non-numerical values and the column number.
      newColumn.row_label = column.row_label;
      newColumn.column_index = column.column_index;

      return newColumn;
    });
  }

  if (reversed) {
    return [...result].reverse();
  }

  return result;
}

export function openDrawerForDrillIn(columnId, rowId, meta, eventTarget) {
  if (meta.drill_in.publishing) {
    openDrawerForDrillInPublish(columnId, rowId, meta, eventTarget);
    return;
  }

  const relative_url = meta.drill_in.base_url;
  const filterParams = meta.drill_in.filter_params;
  const cellParams = getCellParams(columnId, rowId, filterParams);

  // If we didn't find any cell params, we don't have a url to return.
  if (cellParams === undefined) return;

  window.AhaAnalytics.track(
    'Feature-7381932578553799632',
    'User views chart data in report',
    {
      'Chart type': meta.drill_in.chart_type,
    }
  );

  const url = new URL(relative_url, window.location.toString());
  AhaDrawer.showUrl(url.toString(), { data: cellParams, usePost: true });
}

function openDrawerForDrillInPublish(columnId, rowId, meta, eventTarget) {
  // If the chart is in a dashboard panel, we need to pass the panel ID to the backend
  const parentPanel = eventTarget?.closest('div[id^="Dashboard-Panel-"]');
  const panelId = parentPanel?.id?.split('-')?.at(-1);

  const pathSegments = new URL(window.location.href).pathname.split('/');
  const isIdeaPortalUrl = pathSegments.indexOf('sections') > -1;

  let webpageToken;
  if (isIdeaPortalUrl) {
    webpageToken = pathSegments[pathSegments.indexOf('sections') + 1];
  } else {
    webpageToken = pathSegments[pathSegments.indexOf('shared') + 1];
  }

  let configurationToken = null;
  if (isIdeaPortalUrl) {
    configurationToken = window.ideaPortalInteractiveConfigurationToken;
  } else {
    // If shared filters are activated, we need to pass the interactive configuration token to the backend
    const configurationIndex = pathSegments.indexOf('configuration');
    if (configurationIndex !== -1) {
      configurationToken = pathSegments[configurationIndex + 1];
    }
  }

  const params = new URLSearchParams({ column_id: columnId });

  if (rowId !== null && rowId !== undefined) params.set('row_id', rowId);
  if (panelId) params.set('panel_id', panelId);
  if (configurationToken) params.set('configuration_token', configurationToken);

  let getUrl;
  if (isIdeaPortalUrl) {
    getUrl = `/shared/bookmark/${webpageToken}/interactive_charts?${params.toString()}`;
  } else {
    getUrl = `/shared/${webpageToken}/bookmark/${
      meta.drill_in.report_id
    }/interactive_charts?${params.toString()}`;
  }

  AhaDrawer.showUrl(getUrl);
}

export function onIdeaPortalSettingsPage() {
  return window.location.pathname.includes('settings/account/idea_portals');
}

function getCellParams(columnId, rowId, filterParams) {
  if (rowId === undefined || rowId === null) {
    return filterParams[columnId];
  } else {
    return filterParams[columnId]?.[rowId];
  }
}

function trimDeduplicatedLabel(series) {
  // When the same series name is used multiple times, repeat usages have a zero-width space
  // appended to them to visually appear the same, but have Nivo treat them as distinct. See
  // 7a4f87a69aa and label_deduplicator.rb.
  return series && series.replace(/\u{200B}+$/u, '');
}
